import { useEffect, useReducer, useRef } from 'react';
import {
  autoCreateEventStreamUrl, getAutoCreateRun, mintAutoCreateStreamToken,
} from '../services/api';
import { createReconnectingEventSource } from '../lib/reconnectingEventSource';

/**
 * React hook that subscribes to an Auto Create run's SSE event stream
 * and exposes a structured live view: per-phase status, the rolling
 * activity feed, current run state, and the latest run snapshot.
 *
 * Usage:
 *
 *   const { run, phases, feed, status, reconnecting, error, cursor } =
 *       useAutoCreateRun(runId);
 *
 * Lifecycle:
 *
 *   1. Mount → fetch the run snapshot (so reconnects / page refreshes
 *      restore correct state) AND open EventSource against
 *      /runs/{runId}/events?after=<cursor>.
 *   2. On every event → reducer updates per-phase status + feed lines +
 *      cursor (the highest sequence we've seen).
 *   3. On disconnect → after a small backoff, reconnect with the
 *      current cursor; replay-from-cursor on the server tops us up
 *      with anything we missed.
 *   4. On terminal status (completed / failed / cancelled / rejected) →
 *      close the EventSource.
 *
 * Auth note: EventSource doesn't let us set custom headers. The
 * platform's API auth lives in cookies so this works as-is for the
 * app's shipped auth flow. If a tenant runs JWT-only without cookies,
 * a polling fallback can be added behind a feature flag.
 */

const PHASES = ['parse', 'intent', 'match', 'gaps', 'draft', 'guards', 'review'];

const PHASE_INITIAL = () => Object.fromEntries(
  PHASES.map((p) => [p, { status: 'pending', durationMs: null, lastMessage: null }])
);

const initialState = {
  run:           null,            // snapshot from GET /runs/{runId}
  status:        'pending',
  currentPhase:  null,
  phases:        PHASE_INITIAL(), // { parse: { status, durationMs, lastMessage }, ... }
  feed:          [],              // [{ sequence, level, phase, message, payload, occurredAt }]
  cursor:        0,
  error:         null,
  reconnecting:  false,
  // Surfaced when the pipeline reaches its two human-gated pauses:
  pausedGap:     null,            // payload of gap_detected events keyed by stepId
  reviewPayload: null,            // payload of run_requires_review event
};

function reducer(state, action) {
  switch (action.type) {
    case 'snapshot': {
      const r = action.run;
      return {
        ...state,
        run: r,
        status: r?.status || state.status,
        currentPhase: r?.currentPhase || state.currentPhase,
      };
    }
    case 'event': {
      const e = action.event;
      const cursor = Math.max(state.cursor, e.sequence || 0);
      const phases = { ...state.phases };
      let status = state.status;
      let currentPhase = state.currentPhase;
      let pausedGap = state.pausedGap;
      let reviewPayload = state.reviewPayload;

      if (e.phase && phases[e.phase]) {
        const p = { ...phases[e.phase] };
        if (e.eventType === 'phase_started') {
          p.status = 'running';
          p.lastMessage = e.message;
          currentPhase = e.phase;
        } else if (e.eventType === 'phase_progress') {
          p.lastMessage = e.message;
        } else if (e.eventType === 'phase_completed') {
          p.status = 'done';
          p.durationMs = e.payload?.durationMs ?? p.durationMs;
          p.lastMessage = e.message;
        }
        phases[e.phase] = p;
      }

      if (e.eventType === 'gap_detected') {
        // Aggregate gaps by stepId so multiple gap_detected events
        // accumulate into one map the UI iterates.
        const stepId = e.payload?.stepId;
        if (stepId) {
          pausedGap = { ...(pausedGap || {}), [stepId]: e.payload };
        }
        // Mark the GAPS chip blocked.
        if (phases.gaps) phases.gaps = { ...phases.gaps, status: 'blocked', lastMessage: e.message };
        /* G77 fix: flip top-level status so AutoCreateWizard
         * transitions from PipelineScreen → CapabilityGapScreen.
         * Without this, the wizard kept rendering the live event
         * feed even after the pipeline paused — the user had no UI
         * to resolve the gap and the run sat at status=paused_gap
         * indefinitely. The previous code only set status on
         * `run_requires_review` (line 102-106) but missed the gap
         * pause. Backend confirms run.status=='paused_gap' via the
         * GET /runs/{id} response — see Pass 18 trace. */
        status = 'paused_gap';
      }
      if (e.eventType === 'gap_resolved') {
        /* When the user resolves all gaps and the backend resumes
         * the pipeline, the run status flips back to 'running'. The
         * wizard then re-renders PipelineScreen so the user sees
         * DRAFT/GUARDS/REVIEW phases tick through. */
        status = 'running';
      }
      if (e.eventType === 'run_requires_review') {
        reviewPayload = e.payload;
        status = 'awaiting_review';
        phases.review = { ...phases.review, status: 'done', lastMessage: e.message };
      }
      // ── Terminal-state plumbing ──
      //
      // PhaseEventPublisher.publishRunCompleted ships the new agent's
      // id in the event payload as {agentId: "<uuid>"}. The bootstrap
      // snapshot we fetched at mount time predates that activity, so
      // it doesn't have draftedAgentId set; if we don't merge the
      // payload here, the PipelineScreen's "View AutoLets-… in Build →"
      // banner never shows because its gate
      //   `live.run?.draftedAgentId || live.run?.agentId`
      // resolves to undefined. Same gap on rejected/cancelled (no
      // agent id, that's fine) and failed (carries error metadata
      // we surface elsewhere).
      let run = state.run;
      if (e.eventType === 'run_completed') {
        status = 'completed';
        const agentIdFromPayload = e.payload?.agentId;
        const agentNameFromPayload = e.payload?.agentName;
        if (agentIdFromPayload || agentNameFromPayload) {
          run = { ...(run || {}) };
          if (agentIdFromPayload)   run.draftedAgentId = agentIdFromPayload;
          // The post-rename agent name takes precedence over the
          // pre-rename proposedAgentName captured at run-start time —
          // ensures the success banner reads "AutoLets-… (2)" rather
          // than the original collided name.
          if (agentNameFromPayload) run.proposedAgentName = agentNameFromPayload;
        }
      }
      if (e.eventType === 'run_failed')      status = 'failed';
      if (e.eventType === 'run_cancelled')   status = 'cancelled';
      if (e.eventType === 'run_rejected')    status = 'rejected';

      return {
        ...state,
        run,
        cursor,
        feed: [...state.feed, {
          sequence:   e.sequence,
          phase:      e.phase,
          eventType:  e.eventType,
          level:      e.level,
          message:    e.message,
          payload:    e.payload,
          occurredAt: e.occurredAt,
        }],
        phases,
        status,
        currentPhase,
        pausedGap,
        reviewPayload,
        reconnecting: false,
        error: null,
      };
    }
    case 'reconnecting': return { ...state, reconnecting: true };
    case 'error':        return { ...state, error: action.error, reconnecting: false };
    case 'reset':        return { ...initialState, phases: PHASE_INITIAL() };
    default:             return state;
  }
}

export function useAutoCreateRun(runId) {
  const [state, dispatch] = useReducer(reducer, initialState, () => ({ ...initialState, phases: PHASE_INITIAL() }));
  const cursorRef = useRef(0);
  const cancelledRef = useRef(false);
  const sourceRef = useRef(null);

  useEffect(() => {
    cursorRef.current = state.cursor;
  }, [state.cursor]);

  useEffect(() => {
    if (!runId) return undefined;
    cancelledRef.current = false;
    dispatch({ type: 'reset' });

    // Bootstrap: fetch snapshot in parallel with opening the stream.
    (async () => {
      try {
        const res = await getAutoCreateRun(runId);
        if (!cancelledRef.current && res?.ok && res.data) {
          dispatch({ type: 'snapshot', run: res.data });
        }
      } catch (e) {
        // Snapshot failure is non-fatal — the stream will populate state.
      }
    })();

    // ── Per-connect URL provider (stream-token auth, 2026-05-04 hardened) ──
    // Each (re)connect mints a fresh single-use token via the header-
    // authenticated POST endpoint so the bearer JWT never touches the SSE URL.
    // Tokens are 60s TTL and consumed on connect — frequent re-mints are by
    // design. The shared createReconnectingEventSource calls this async `url`
    // provider before every (re)connect, so the token mint AND the cursor-aware
    // ?after= URL are rebuilt each time. Throwing makes the helper back off +
    // retry on its capped-backoff engine — replacing the hand-rolled reconnect
    // loop this hook used to carry (one reconnect engine for all SSE consumers).
    const resolveStreamUrl = async () => {
      const tokenRes = await mintAutoCreateStreamToken(runId);
      if (!tokenRes?.ok || !tokenRes.data?.token) {
        // Mint failed — most likely the session expired (401). Surface a
        // hook-level error so the UI can prompt re-auth, then throw so the
        // helper backs off and retries (the user may refresh their session).
        dispatch({ type: 'error', error: tokenRes?.error || 'Could not mint stream token' });
        throw new Error('stream-token mint failed');
      }
      return autoCreateEventStreamUrl(runId,
              { afterSequence: cursorRef.current, streamToken: tokenRes.data.token });
    };

    // The server uses `event:` named events; register a listener per known
    // type so the eventType is preserved into the reducer.
    const allEventTypes = [
      'phase_started', 'phase_progress', 'phase_completed',
      'gap_detected', 'gap_resolved',
      'run_requires_review', 'run_completed', 'run_failed',
      'run_cancelled', 'run_rejected',
    ];
    const onMessageData = (data) => {
      try {
        const parsed = typeof data === 'string' ? JSON.parse(data) : data;
        dispatch({ type: 'event', event: parsed });
      } catch (e) {
        // Malformed payload — don't crash the stream.
        // eslint-disable-next-line no-console
        console.warn('[AutoCreate/SSE] malformed event payload', e);
      }
    };
    const events = {};
    allEventTypes.forEach((t) => { events[t] = (ev) => onMessageData(ev.data); });

    sourceRef.current = createReconnectingEventSource({
      url: resolveStreamUrl,          // async: re-mints token + carries cursor each connect
      withCredentials: true,
      baseDelayMs: 500,
      maxDelayMs: 5000,
      events,
      onError: () => { if (!cancelledRef.current) dispatch({ type: 'reconnecting' }); },
    });

    return () => {
      cancelledRef.current = true;
      if (sourceRef.current) {
        try { sourceRef.current.close(); } catch (_) {}
        sourceRef.current = null;
      }
    };
  }, [runId]);

  // Close the stream as soon as we hit a terminal status — saves a
  // long-lived connection sitting open after the run is done.
  useEffect(() => {
    if (!sourceRef.current) return;
    const TERMINAL = new Set(['completed', 'failed', 'cancelled', 'rejected']);
    if (TERMINAL.has(state.status)) {
      try { sourceRef.current.close(); } catch (_) {}
      sourceRef.current = null;
    }
  }, [state.status]);

  return state;
}
